// Import the functions you need from the SDKs you need
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  serverTimestamp,
  deleteDoc,
  updateDoc,
  addDoc,
} from 'firebase/firestore';
import {
  getAnalytics,
  isSupported,
  logEvent as gaLogEvent,
} from 'firebase/analytics';
// (no imports needed from achievements here)

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: 'AIzaSyBbQPSn7dJRAwxoEIT4b8RfQM-kPxjYWfU',
  authDomain: 'kidsfit-34279.firebaseapp.com',
  projectId: 'kidsfit-34279',
  storageBucket: 'kidsfit-34279.firebasestorage.app',
  messagingSenderId: '1001817877361',
  appId: '1:1001817877361:web:6a1e8d6a3dabf3c38d24d0',
  measurementId: 'G-24XTSF6NLK',
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// ---------- Analytics ----------
let analytics = null;
if (typeof window !== 'undefined') {
  // Analytics requires browser environment and secure origin (https or localhost)
  isSupported()
    .then((supported) => {
      if (supported) {
        analytics = getAnalytics(app);
      }
    })
    .catch(() => {
      // noop if analytics not supported
    });
}

export const trackEvent = (eventName, params = {}) => {
  try {
    if (analytics) {
      gaLogEvent(analytics, eventName, params);
    }
  } catch (_) {
    // swallow analytics errors
  }
};

// ---------- Public mirror helpers ----------
export const upsertPlayerPublic = async ({
  playerId,
  displayName,
  avatar,
  totalPoints,
}) => {
  const pubRef = doc(db, 'playerPublic', playerId);
  await setDoc(
    pubRef,
    {
      playerId,
      displayName,
      avatar: avatar || '🐶',
      totalPoints: totalPoints || 0,
      lastActive: serverTimestamp(),
    },
    { merge: true }
  );
};

export const addPublicEvent = async (
  playerId,
  { type, id, title = null, sport = null, points = 0 }
) => {
  const eventsRef = collection(db, 'playerPublic', playerId, 'events');
  await addDoc(eventsRef, {
    type, // 'workout' | 'skill' | 'exercise'
    id,
    title,
    sport,
    points,
    completedAt: serverTimestamp(),
  });
};

export const deletePlayerPublic = async (playerId) => {
  const pubRef = doc(db, 'playerPublic', playerId);
  const eventsRef = collection(db, 'playerPublic', playerId, 'events');
  const snap = await getDocs(eventsRef);
  if (!snap.empty) {
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
  }
  await deleteDoc(pubRef);
};

// Function to generate unique player ID
const generateUniquePlayerId = async (playerName) => {
  try {
    // Check if the base name exists
    const baseName = playerName.trim();
    let playerId = baseName;
    let counter = 0;

    // Keep checking until we find an available ID
    while (true) {
      const playerIdRef = doc(db, 'playerIds', playerId);
      const playerIdDoc = await getDoc(playerIdRef);

      if (!playerIdDoc.exists()) {
        // This ID is available
        return playerId;
      }

      // ID exists, try with number suffix
      counter++;
      playerId = `${baseName}${counter}`;
    }
  } catch (error) {
    console.error('Error generating unique player ID:', error);
    throw error;
  }
};

// Auth functions
export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

export const signInWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error('Error signing in with email:', error);
    throw error;
  }
};

export const registerWithEmail = async (email, password) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error('Error registering with email:', error);
    throw error;
  }
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

export const signOut = async () => {
  try {
    await auth.signOut();
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Firestore functions
export const createUserProfile = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(
      userRef,
      {
        ...userData,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const userProfileExists = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const snap = await getDoc(userRef);
    return snap.exists();
  } catch (error) {
    console.error('Error checking user profile existence:', error);
    return false;
  }
};

export const saveConsentAgreement = async (userId) => {
  try {
    const consentRef = doc(db, 'consentAgreements', userId);
    await setDoc(consentRef, {
      userId: userId,
      agreed: true,
      agreementVersion: '1.0',
      agreedAt: serverTimestamp(),
      agreementText: 'Safety & Liability Agreement - KidsFit',
    });
  } catch (error) {
    console.error('Error saving consent agreement:', error);
    throw error;
  }
};

export const getConsentAgreement = async (userId) => {
  try {
    const consentRef = doc(db, 'consentAgreements', userId);
    const consentDoc = await getDoc(consentRef);

    if (consentDoc.exists()) {
      return consentDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting consent agreement:', error);
    throw error;
  }
};

export const verifyParentPin = async (userId, pin) => {
  try {
    const normalizePin = (value) => {
      if (value === undefined || value === null) return null;
      const digits = String(value).replace(/\D/g, '').slice(0, 4);
      return digits.length === 4 ? digits : null;
    };

    const inputPin = normalizePin(pin);
    if (!inputPin) return false;

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.exists() ? userDoc.data() : null;

    const savedPin = normalizePin(userData?.parentPin);
    if (!savedPin) return false;

    // Handle legacy numbers without leading zeros: pad to 4 then compare
    const pad4 = (p) => (p && p.length < 4 ? p.padStart(4, '0') : p);
    return (
      inputPin === savedPin ||
      inputPin === pad4(savedPin) ||
      pad4(inputPin) === savedPin
    );
  } catch (error) {
    console.error('Error verifying PIN:', error);
    return false;
  }
};

export const createPlayer = async (
  userId,
  playerName,
  avatar = '🐶',
  age = null
) => {
  try {
    // Generate unique player ID
    const playerId = await generateUniquePlayerId(playerName);

    // Create player in user's collection
    const playerRef = doc(collection(db, 'users', userId, 'players'));
    await setDoc(playerRef, {
      id: playerRef.id,
      playerId: playerId, // Store the unique player ID
      playerName,
      avatar,
      age: typeof age === 'number' ? age : null,
      totalScore: 0,
      streakDays: 0,
      createdAt: serverTimestamp(),
    });

    // Create entry in global playerIds collection
    const globalPlayerRef = doc(db, 'playerIds', playerId);
    await setDoc(globalPlayerRef, {
      playerId: playerId,
      displayName: playerName,
      userId: userId,
      avatar: avatar,
      age: typeof age === 'number' ? age : null,
      totalPoints: 0,
      lastActive: serverTimestamp(),
      createdAt: serverTimestamp(),
    });

    // Create public mirror profile
    await upsertPlayerPublic({
      playerId,
      displayName: playerName,
      avatar,
      totalPoints: 0,
    });

    return playerRef.id;
  } catch (error) {
    console.error('Error creating player:', error);
    throw error;
  }
};

export const getPlayers = async (userId) => {
  try {
    const playersSnapshot = await getDocs(
      collection(db, 'users', userId, 'players')
    );
    const players = playersSnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Ensure avatar exists for backward compatibility
        avatar: data.avatar || '🐶',
      };
    });
    return players;
  } catch (error) {
    console.error('Error getting players:', error);
    throw error;
  }
};

// Helper function to determine completion type from ID pattern
// Current data model:
// - Workouts use small IDs (1..16)
// - Skills use IDs 501..2000
// - Drills use IDs 2001..4000
// We do not write exercise completions individually.
const getCompletionType = (completionId) => {
  if (completionId >= 2001 && completionId <= 4000) {
    return 'drill';
  } else if (completionId >= 501 && completionId <= 2000) {
    return 'skill';
  } else if (completionId >= 1 && completionId <= 500) {
    return 'workout';
  } else {
    return 'unknown';
  }
};

// Utility function to check if a player has completed a specific workout/skill
export const hasCompleted = (player, completionId) => {
  const completionType = getCompletionType(completionId);

  switch (completionType) {
    case 'exercise':
      // Exercises are not tracked individually
      return false;
    case 'workout':
      return (player.workoutCompletions || []).find(
        (c) => c.id === completionId
      );
    case 'skill':
    case 'drill':
      // Both skills and drills are tracked in skillCompletions
      return (player.skillCompletions || []).find((c) => c.id === completionId);
    default:
      return false;
  }
};

// Utility function to get completion count for a specific type
export const getCompletionCount = (player, completionType) => {
  switch (completionType) {
    case 'skill':
      return (player.skillCompletions || []).length;
    case 'workout':
      return (player.workoutCompletions || []).length;
    default:
      return 0;
  }
};

export const saveWorkoutCompletion = async (completionData) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const userId = user.uid;
    const timestamp = new Date();

    // Determine completion type and ID
    const completionId = completionData.workoutId;
    console.log('[SAVE] Begin saveWorkoutCompletion', {
      userId,
      completionId,
      points: completionData.points,
      playersCount: (completionData.players || []).length,
    });

    // Determine type from ID pattern
    const completionType = getCompletionType(completionId);
    const isExercise = completionType === 'exercise';
    const isWorkout = completionType === 'workout';
    const isSkill = completionType === 'skill';
    const isDrill = completionType === 'drill';

    // Update each player's data
    for (const player of completionData.players) {
      // Support callers that pass either the Firestore doc id (player.id)
      // or the friendly unique playerId string (player.playerId)
      const targetPlayerDocId = player.id || player.playerId;
      const playerRef = doc(db, 'users', userId, 'players', targetPlayerDocId);
      console.log(
        '[SAVE] Target player doc path:',
        `users/${userId}/players/${targetPlayerDocId}`
      );

      // Get current player data
      const playerDoc = await getDoc(playerRef);
      const playerData = playerDoc.data() || {};
      console.log('[SAVE] Loaded playerDoc exists=', playerDoc.exists(), {
        playerName: playerData.playerName,
        playerId: playerData.playerId,
        currentTotalScore: playerData.totalScore,
      });

      console.log('Player data from user collection:', playerData);
      console.log('Player ID from completion data:', player.playerId);

      // Update total score
      const currentTotalScore = playerData.totalScore || 0;
      const newTotalScore = currentTotalScore + completionData.points;
      console.log('[SAVE] Points update', {
        add: completionData.points,
        currentTotalScore,
        newTotalScore,
      });

      // Update streak
      const lastWorkoutDate = playerData.lastWorkoutDate
        ? new Date(playerData.lastWorkoutDate.toDate())
        : null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let newStreak = playerData.streak || 0;
      if (lastWorkoutDate) {
        lastWorkoutDate.setHours(0, 0, 0, 0);
        const daysDiff = Math.floor(
          (today - lastWorkoutDate) / (1000 * 60 * 60 * 24)
        );

        if (daysDiff === 1) {
          newStreak += 1;
        } else if (daysDiff > 1) {
          newStreak = 1;
        }
      } else {
        newStreak = 1;
      }

      // Update completion tracking with objects containing timestamps
      let workoutCompletions = playerData.workoutCompletions || [];
      let skillCompletions = playerData.skillCompletions || [];

      const completion = {
        id: completionId,
        completedAt: timestamp,
      };

      if (isExercise) {
        // Exercises are not tracked individually (they're part of workouts/skills)
        console.log('Exercise completion not tracked individually');
      } else if (isWorkout) {
        // For regular workouts
        if (!workoutCompletions.find((c) => c.id === completionId)) {
          workoutCompletions.push(completion);
        }
      } else if (isSkill || isDrill) {
        // For skills and drills (both tracked in skillCompletions)
        if (!skillCompletions.find((c) => c.id === completionId)) {
          skillCompletions.push(completion);
        }
      }

      // Update player document
      await updateDoc(playerRef, {
        totalScore: newTotalScore,
        streak: newStreak,
        lastWorkoutDate: timestamp,
        workoutCompletions: workoutCompletions,
        skillCompletions: skillCompletions,
        lastUpdated: timestamp,
      });
      console.log('[SAVE] Player doc updated successfully');

      // Sync points to global playerIds collection
      if (playerData.playerId) {
        console.log(
          'Updating global playerIds collection for playerId:',
          playerData.playerId
        );
        console.log('New total score:', newTotalScore);
        const globalPlayerRef = doc(db, 'playerIds', playerData.playerId);
        await updateDoc(globalPlayerRef, {
          totalPoints: newTotalScore,
          lastActive: timestamp,
        });
        console.log('Global playerIds collection updated successfully');

        // Update public profile mirror and append a public event
        await upsertPlayerPublic({
          playerId: playerData.playerId,
          displayName: playerData.playerName,
          avatar: playerData.avatar,
          totalPoints: newTotalScore,
        });

        const publicType = isWorkout
          ? 'workout'
          : isSkill || isDrill
            ? 'skill'
            : 'exercise';
        await addPublicEvent(playerData.playerId, {
          type: publicType,
          id: completionId,
          title: null,
          sport: null,
          points: completionData.points,
        });
      } else {
        console.log('No playerId found in playerData:', playerData);
      }
    }

    console.log('[SAVE] Completed saveWorkoutCompletion for', completionId);
    return { success: true };
  } catch (error) {
    console.error('Error saving workout completion:', error);
    throw error;
  }
};

export const getPlayerStats = async (userId, playerId) => {
  try {
    const playerDoc = await getDoc(
      doc(db, 'users', userId, 'players', playerId)
    );
    if (!playerDoc.exists()) {
      throw new Error('Player not found');
    }

    const playerData = playerDoc.data();

    // Get recent completions from the arrays (sorted by completedAt)
    const allCompletions = [
      ...(playerData.workoutCompletions || []),
      ...(playerData.skillCompletions || []),
    ].sort(
      (a, b) =>
        new Date(b.completedAt.toDate()) - new Date(a.completedAt.toDate())
    );

    const recentCompletions = allCompletions.slice(0, 10);

    return {
      ...playerData,
      // Ensure avatar exists for backward compatibility
      avatar: playerData.avatar || '🐶',
      recentCompletions,
    };
  } catch (error) {
    console.error('Error getting player stats:', error);
    throw error;
  }
};

// Function to update existing players without avatars
export const updatePlayerAvatar = async (userId, playerId, avatar) => {
  try {
    const playerRef = doc(db, 'users', userId, 'players', playerId);
    await setDoc(playerRef, { avatar }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating player avatar:', error);
    throw error;
  }
};

// Function to update player details (name and avatar)
export const updatePlayerDetails = async (
  userId,
  playerId,
  playerName,
  avatar,
  age
) => {
  try {
    const playerRef = doc(db, 'users', userId, 'players', playerId);
    const update = { playerName, avatar };
    if (typeof age === 'number') {
      update.age = age;
    }
    await setDoc(playerRef, update, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating player details:', error);
    throw error;
  }
};

export const withdrawPoints = async (userId, playerId, amount, pin) => {
  try {
    // Verify parent PIN
    const isPinValid = await verifyParentPin(userId, pin);
    if (!isPinValid) {
      throw new Error('Invalid PIN');
    }

    // Get current player data
    const playerRef = doc(db, 'users', userId, 'players', playerId);
    const playerDoc = await getDoc(playerRef);
    const playerData = playerDoc.data();

    const currentPoints = playerData.totalScore || 0;

    // Check if player has enough points
    if (currentPoints < amount) {
      throw new Error('Insufficient points');
    }

    // Update player's points
    const newTotalScore = currentPoints - amount;

    await setDoc(
      playerRef,
      {
        totalScore: newTotalScore,
        lastWithdrawal: {
          amount,
          date: serverTimestamp(),
        },
      },
      { merge: true }
    );

    // Sync to global collection
    if (playerData.playerId) {
      const globalPlayerRef = doc(db, 'playerIds', playerData.playerId);
      await updateDoc(globalPlayerRef, {
        totalPoints: newTotalScore,
      });

      // Update public profile mirror after withdrawal
      await upsertPlayerPublic({
        playerId: playerData.playerId,
        displayName: playerData.playerName,
        avatar: playerData.avatar,
        totalPoints: newTotalScore,
      });
    }

    return {
      success: true,
      newBalance: newTotalScore,
      withdrawn: amount,
    };
  } catch (error) {
    console.error('Error withdrawing points:', error);
    throw error;
  }
};

export const deletePlayer = async (userId, playerId, pin) => {
  try {
    // Verify parent PIN
    const isPinValid = await verifyParentPin(userId, pin);
    if (!isPinValid) {
      throw new Error('Invalid PIN');
    }

    // Get player data first so we can clean up related global records
    const playerRef = doc(db, 'users', userId, 'players', playerId);
    const playerSnap = await getDoc(playerRef);
    const playerData = playerSnap.exists() ? playerSnap.data() : null;

    // Delete all associated workout data (if any)
    const workoutsRef = collection(
      db,
      'users',
      userId,
      'players',
      playerId,
      'workouts'
    );
    const workoutsSnapshot = await getDocs(workoutsRef);
    if (!workoutsSnapshot.empty) {
      const deletePromises = workoutsSnapshot.docs.map((docRef) =>
        deleteDoc(docRef.ref)
      );
      await Promise.all(deletePromises);
    }

    // Delete the player document from the user's collection
    await deleteDoc(playerRef);

    // Delete the global playerIds record if present
    if (playerData?.playerId) {
      const globalPlayerRef = doc(db, 'playerIds', playerData.playerId);
      await deleteDoc(globalPlayerRef);

      // Also delete the public mirror and events
      await deletePlayerPublic(playerData.playerId);
    }

    return {
      success: true,
      message: 'Player deleted successfully',
    };
  } catch (error) {
    console.error('Error deleting player:', error);
    throw error;
  }
};

export const getGlobalLeaderboard = async (limitCount = 100) => {
  try {
    const leaderboardQuery = query(
      collection(db, 'playerIds'),
      orderBy('totalPoints', 'desc'),
      limit(limitCount)
    );
    const leaderboardSnapshot = await getDocs(leaderboardQuery);

    const leaderboard = leaderboardSnapshot.docs.map((doc, index) => {
      const data = doc.data();
      return {
        rank: index + 1,
        playerId: data.playerId,
        displayName: data.displayName,
        avatar: data.avatar || '🐶',
        totalPoints: data.totalPoints || 0,
        lastActive: data.lastActive,
      };
    });

    return leaderboard;
  } catch (error) {
    console.error('Error getting global leaderboard:', error);
    throw error;
  }
};

export const getFamilyLeaderboard = async (userId) => {
  try {
    const players = await getPlayers(userId);

    // Sort players by total score
    const sortedPlayers = players
      .map((player, index) => ({
        rank: index + 1,
        playerId: player.playerId || player.playerName, // Fallback for existing players
        displayName: player.playerName,
        avatar: player.avatar || '🐶',
        totalPoints: player.totalScore || 0,
        lastActive: player.lastWorkoutDate,
      }))
      .sort((a, b) => b.totalPoints - a.totalPoints)
      .map((player, index) => ({
        ...player,
        rank: index + 1,
      }));

    return sortedPlayers;
  } catch (error) {
    console.error('Error getting family leaderboard:', error);
    throw error;
  }
};

export { auth, db, analytics };
